<template>
  <div class="quotation-row-editor py-3 px-2 my-1">
    <v-row dense align="start">
      <v-col cols="12" md="9" class="d-flex align-start">
        <v-icon class="mr-2 mt-2 no-gradient" :title="$t(`quotations.calc.row_types.${rowTypeKey}`)">{{ typeIcon }}</v-icon>
        <v-autocomplete
          v-if="row.rowType === 'WORK'"
          :value="row.workMethodId"
          :items="workMethods"
          :label="$t('quotations.calc.method')"
          :disabled="saving"
          item-text="name"
          item-value="id"
          autofocus
          outlined
          dense
          hide-details
          @change="changeMethod"
        />
        <item-combobox
          v-else-if="row.rowType === 'ITEM'"
          :value="row.item"
          :items="items"
          :label="$t('quotations.calc.choose_item')"
          :disabled="saving"
          autofocus
          hide-details
          class="flex-grow-1"
          @change="changeItem"
        />
        <v-textarea
          v-else
          :value="row.description"
          :label="$t('quotations.calc.description')"
          :disabled="saving"
          autofocus
          outlined
          dense
          auto-grow
          rows="1"
          hide-details
          @input="v => set('description', v)"
        />
      </v-col>
      <v-col cols="10" md="2" class="text-right body-2 pt-3">
        <span v-if="row.conditional" class="caption grey--text">{{ $t('quotations.calc.not_in_total') }}</span>
        <template v-else>
          <span v-if="total !== null" class="font-weight-medium">{{ $formatNumber(total, '0,0.00') }} €</span>
          <span v-else class="grey--text">–</span>
        </template>
      </v-col>
      <v-col cols="2" md="1" class="text-right text-no-wrap">
        <v-btn icon small color="primary" :disabled="saving" :title="$t('quotations.calc.done_editing')" @click="$emit('done')">
          <v-icon class="no-gradient">mdi-check</v-icon>
        </v-btn>
        <v-btn icon small :disabled="saving" :title="$t('quotations.calc.delete_row')" @click="$emit('remove')">
          <v-icon class="no-gradient" color="error">delete</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <div class="row-details">
      <template v-if="row.rowType === 'WORK'">
        <attribute-form v-if="method && defs.length > 0" :defs="defs" :value="row.attributeValues || []" :disabled="saving" class="mt-3" @input="changeAttributes" />
        <v-row dense class="mt-1">
          <v-col cols="12" md="8">
            <v-textarea
              :value="row.description"
              :label="$t('quotations.calc.work_description')"
              :disabled="saving"
              outlined
              dense
              auto-grow
              rows="1"
              hide-details
              @input="v => set('description', v)"
            />
          </v-col>
          <v-col cols="12" md="4">
            <numeric-input
              :value="row.unitPrice"
              :options="priceOptions"
              :label="$t('quotations.calc.unit_price')"
              :suffix="unitSuffix"
              :hint="pricingHint"
              :disabled="saving"
              persistent-hint
              outlined
              dense
              @input="v => set('unitPrice', v)"
            />
          </v-col>
        </v-row>
      </template>

      <v-row v-else dense align="center" class="mt-1">
        <v-col v-if="row.rowType === 'ITEM'" cols="12" md="5">
          <v-text-field :value="row.description" :label="$t('quotations.calc.description')" :disabled="saving" outlined dense hide-details @input="v => set('description', v)" />
        </v-col>
        <v-col v-if="!row.conditional" cols="4" md="2">
          <numeric-input :value="row.quantity" :label="$t('quotations.calc.quantity')" :disabled="saving" outlined dense hide-details reverse @input="v => set('quantity', v)" />
        </v-col>
        <v-col cols="4" md="2">
          <v-combobox :value="row.unit" :items="row.conditional ? conditionalUnits : units" :label="$t('quotations.calc.unit')" :disabled="saving" outlined dense hide-details @input="v => set('unit', v)" />
        </v-col>
        <v-col cols="4" md="3">
          <!-- NumericInput ei seuraa ulkoisia muutoksia: nimikkeestä esitäytetty hinta näkyy, kun kenttä luodaan uudelleen -->
          <numeric-input
            :key="`price-${row.item ? row.item.id : ''}`"
            :value="row.unitPrice"
            :options="priceOptions"
            :label="$t('quotations.calc.unit_price')"
            :suffix="unitSuffix"
            :disabled="saving"
            outlined
            dense
            hide-details
            @input="v => set('unitPrice', v)"
          />
        </v-col>
        <v-col v-if="row.conditional" cols="12" md="5">
          <v-text-field :value="row.conditionText" :label="$t('quotations.calc.condition')" :disabled="saving" outlined dense hide-details @input="v => set('conditionText', v)" />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import AttributeForm from '@/components/Work/AttributeForm';
import ItemCombobox from '@/components/ItemCombobox';
import NumericInput from '@/components/NumericInput';
import { rowTotal, workDefs, pricingDef, pricedQuantity, pricedUnit, priceUnit, itemUnitPrice } from '@/helpers/quotationCalc';
import { UNITS } from '@/helpers/quotationStatus';

export default {
  name: 'QuotationRowEditor',
  components: { AttributeForm, ItemCombobox, NumericInput },
  props: {
    row: { type: Object, required: true },
    workMethods: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      units: UNITS,
      conditionalUnits: ['€/m', '€/h', '€/kpl', '€/vrk', '%', 'm'],
      // á-hinta aina kahdella desimaalilla, jotta rivi lukee samoin kuin Yhteensä-sarake
      priceOptions: { decimalCharacter: ',', digitGroupSeparator: ' ', decimalPlaces: 2, allowDecimalPadding: true, emptyInputBehavior: 'null' },
    };
  },
  computed: {
    rowTypeKey() {
      return this.row.conditional ? 'CONDITIONAL' : this.row.rowType;
    },
    typeIcon() {
      if (this.row.conditional) return 'mdi-plus-circle-outline';
      return { WORK: 'mdi-shovel', ITEM: 'mdi-package-variant' }[this.row.rowType] || 'mdi-text';
    },
    method() {
      return this.workMethods.find(m => m.id === this.row.workMethodId);
    },
    defs() {
      return workDefs(this.method);
    },
    total() {
      if (this.row.quantity == null || this.row.unitPrice == null) return null;
      return rowTotal(this.row.quantity, this.row.unitPrice);
    },
    unitSuffix() {
      return priceUnit(this.row.unit);
    },
    // Mistä työrivin määrä tulee: hinnoitteluperusteen parametri tai 1 erä
    pricingHint() {
      const def = pricingDef(this.method);
      return def ? this.$t('quotations.calc.pricing_by_param', { label: def.label }) : this.$t('quotations.calc.pricing_by_lot');
    },
  },
  methods: {
    set(field, value) {
      this.$set(this.row, field, value);
    },
    // Työrivin määrä ja yksikkö seuraavat menetelmän hinnoitteluperustetta
    updatePricedQuantity() {
      this.set('quantity', pricedQuantity(this.method, this.row.attributeValues));
      this.set('unit', pricedUnit(this.method));
    },
    changeMethod(methodId) {
      const method = this.workMethods.find(m => m.id === methodId);
      // Putki säilyy, jos uuden menetelmän putkiparametri sallii sen tyypin
      const oldPipeDef = this.defs.find(d => d.attributeKey === 'pipe_diameter');
      const pipe = oldPipeDef ? ((this.row.attributeValues || []).find(v => v.defId === oldPipeDef.id) || {}).valueItem : null;
      const pipeDef = workDefs(method).find(d => d.attributeKey === 'pipe_diameter');
      this.set('workMethodId', methodId);
      this.set('attributeValues', pipe && pipeDef && (pipeDef.itemTypeIds || []).includes(pipe.typeId) ? [{ defId: pipeDef.id, valueItem: pipe }] : []);
      this.updatePricedQuantity();
    },
    changeAttributes(values) {
      this.set('attributeValues', values);
      this.updatePricedQuantity();
    },
    // Kuvaus täyttyy nimikkeestä, jos sitä ei ole kirjoitettu itse. Yksikkö (käyttöyksikkö) ja sen hinta tulevat aina
    // valitusta nimikkeestä, ettei aiemmin valitun nimikkeen hintaa jää riville.
    changeItem(item) {
      const previousName = this.row.item ? this.row.item.name : '';
      this.set('item', item || null);
      if (!item) return;
      if (!this.row.description || this.row.description === previousName) this.set('description', item.name);
      this.set('unit', item.unit || '');
      this.set('unitPrice', itemUnitPrice(item));
    },
  },
};
</script>

<style scoped>
.quotation-row-editor {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
}
.row-details {
  padding-left: 32px;
}
</style>
